/**
 * The Welcome screen in front of BeHeard's onboarding wizard (CREDITS + WELCOME
 * pass, Part 2; persistence reworked in the onboarding re-audit). It changes
 * nothing about who is onboarded, the steps, or what completes onboarding
 * (utils/onboardingEntry, OnboardingGuard).
 *
 * The only server-backed onboarding state is GET /onboarding/me
 * `hasCompletedOnboarding`: the wizard's steps live in page state until its
 * single final POST, so there is no saved step and no "started" field.
 *
 * WHERE "STARTED" LIVES (final onboarding persistence pass, Q4). On the
 * SERVER: GET /onboarding/me answers `startedAt` + `startedAtKnown` for the
 * Central user in the token's workspace, and Get Started calls
 * POST /onboarding/me/start (table user_onboarding_progress, migration
 * 20261012). So reload, sign-out/in, an app switch AND a new browser or device
 * resume the wizard.
 *
 * The O4 localStorage entry (keyed by Central user AND workspace, the ISO time
 * of Get Started, never cleared on sign-out) is now only the FALLBACK, used
 * while the server cannot say (`startedAtKnown` not true: a server that
 * predates the field, the migration not applied yet, no workspace in the
 * token) and when the POST fails. An old marker the server does not know about
 * is upgraded once (POST) and keeps that browser out of the Welcome meanwhile.
 *
 *   Welcome ⇔ the wizard would show, the profile was READ and says not
 *             completed, no ?retake, the user + workspace are known, not
 *             started, and the user has NO BeHeard data yet (0 sessions)
 *
 * Unknown keeps today's behaviour (the wizard): an unreadable profile, an
 * unknown user/workspace, or an unreadable session history is not "new". A user
 * with sessions but no onboarding profile is a historical user: never sent here
 * (OnboardingGuard lets them in — owner decision 2026-10-08), and on a visit to
 * /onboarding the wizard as today, never the Welcome.
 */

import { parseJwtWorkspaceId } from '@levelup/central-auth';
import type { OnboardingEntry } from './onboardingEntry';

export const ONBOARDING_STARTED_KEY_PREFIX = 'beheard:onboarding-started:v1';

export interface OnboardingIdentity {
  /** Central user id (token `sub`). */
  userId: string;
  /** The workspace the session is in. */
  workspaceId: string;
}

export function onboardingStartedKey(identity: OnboardingIdentity): string {
  return `${ONBOARDING_STARTED_KEY_PREFIX}:${identity.userId}:${identity.workspaceId}`;
}

/** Get Started pressed in this page session when storage refused the write. */
const startedInMemory = new Set<string>();

/** Old markers whose server upgrade was already attempted in this page session. */
const upgradeAttempted = new Set<string>();

/** Test seam: forget the in-memory fallback (and the upgrade attempts). */
export function __resetOnboardingStartedMemoryForTests(): void {
  startedInMemory.clear();
  upgradeAttempted.clear();
}

function storage(): Pick<Storage, 'getItem' | 'setItem'> | null {
  try {
    return (globalThis as { localStorage?: Pick<Storage, 'getItem' | 'setItem'> }).localStorage ?? null;
  } catch {
    return null;
  }
}

export function readOnboardingStarted(identity: OnboardingIdentity | null): boolean {
  if (!identity) return false;
  const key = onboardingStartedKey(identity);
  if (startedInMemory.has(key)) return true;
  try {
    return Boolean(storage()?.getItem(key));
  } catch {
    return false;
  }
}

/** Idempotent: the first Get Started's time is kept. Never throws. */
export function markOnboardingStarted(identity: OnboardingIdentity | null): void {
  if (!identity) return;
  const key = onboardingStartedKey(identity);
  startedInMemory.add(key);
  try {
    const s = storage();
    if (s && !s.getItem(key)) s.setItem(key, new Date().toISOString());
  } catch {
    // Storage unavailable (private mode, blocked): the in-memory entry keeps
    // the wizard for this page session.
  }
}

// ── Server state (Q4) ───────────────────────────────────────────────────────

/** GET /onboarding/me for this user + the token's workspace. 'unknown' = the server could not say. */
export type ServerOnboardingStart = 'started' | 'not-started' | 'unknown';

export function serverOnboardingStart(
  data: { startedAt?: string | null; startedAtKnown?: boolean } | null | undefined,
): ServerOnboardingStart {
  if (!data || data.startedAtKnown !== true) return 'unknown';
  return typeof data.startedAt === 'string' && data.startedAt !== '' ? 'started' : 'not-started';
}

/** The stored (O4) marker only — not this page session's in-memory state. */
export function readStoredOnboardingMarker(identity: OnboardingIdentity | null): boolean {
  if (!identity) return false;
  try {
    return Boolean(storage()?.getItem(onboardingStartedKey(identity)));
  } catch {
    return false;
  }
}

/** Get Started was pressed for this user + workspace in this page session. */
export function startedThisSession(identity: OnboardingIdentity | null): boolean {
  return identity !== null && startedInMemory.has(onboardingStartedKey(identity));
}

export interface OnboardingStartedInput {
  server: ServerOnboardingStart;
  /** The O4 localStorage marker for this user + workspace. */
  storedMarker: boolean;
  startedThisSession: boolean;
}

/** A recorded start from any source keeps the user out of the Welcome. */
export function onboardingStartRecorded(i: OnboardingStartedInput): boolean {
  return i.server === 'started' || i.startedThisSession || i.storedMarker;
}

/** An old client marker the server KNOWS nothing about is written to the server (once). */
export function needsOnboardingMarkerUpgrade(i: Pick<OnboardingStartedInput, 'server' | 'storedMarker'>): boolean {
  return i.server === 'not-started' && i.storedMarker;
}

/** POST /onboarding/me/start (the caller also updates the cached GET /onboarding/me). */
export type PostOnboardingStart = () => Promise<{ startedAt: string; created?: boolean }>;

/**
 * Get Started. The server is the store; the local marker (`markLocal`, i.e.
 * markOnboardingStarted) is written only when the server cannot take it
 * (unknown, or the POST failed — a later load then upgrades it). Never throws.
 */
export async function recordGetStarted(args: {
  identity: OnboardingIdentity;
  server: ServerOnboardingStart;
  postStart: PostOnboardingStart;
  markLocal: () => void;
}): Promise<'server' | 'local'> {
  startedInMemory.add(onboardingStartedKey(args.identity));
  if (args.server === 'unknown') {
    args.markLocal();
    return 'local';
  }
  try {
    await args.postStart();
    return 'server';
  } catch {
    args.markLocal();
    return 'local';
  }
}

/**
 * One-time upgrade of an old client marker: at most one POST per user +
 * workspace per page session. The marker is never removed (it stays the
 * fallback); once the POST succeeded the server answers "started" and no
 * upgrade is needed again. Never throws.
 */
export async function upgradeStoredOnboardingMarker(args: {
  identity: OnboardingIdentity;
  postStart: PostOnboardingStart;
}): Promise<'upgraded' | 'skipped' | 'failed'> {
  const key = onboardingStartedKey(args.identity);
  if (upgradeAttempted.has(key)) return 'skipped';
  upgradeAttempted.add(key);
  try {
    await args.postStart();
    return 'upgraded';
  } catch {
    return 'failed';
  }
}

interface IdentityState {
  centralAuth?: { user?: { id?: string | null } | null; accessToken?: string | null };
  bootContext?: { currentWorkspace?: { id?: string | null } | null };
}

export function selectOnboardingUserId(state: unknown): string | null {
  return (state as IdentityState | null | undefined)?.centralAuth?.user?.id || null;
}

/** The workspace switch-context booted into; else the token's. */
export function selectOnboardingWorkspaceId(state: unknown): string | null {
  const s = state as IdentityState | null | undefined;
  const fromBoot = s?.bootContext?.currentWorkspace?.id;
  if (fromBoot) return fromBoot;
  try {
    return parseJwtWorkspaceId(s?.centralAuth?.accessToken ?? null) || null;
  } catch {
    return null;
  }
}

/** Does the user already have BeHeard data? From GET /gamification/me/overview `stats.totalSessions`. */
export type OnboardingHistory = 'none' | 'some' | 'unknown' | 'loading';

export function onboardingHistory(q: {
  data?: { stats?: { totalSessions?: number | null } | null } | null;
  isLoading?: boolean;
  isFetching?: boolean;
  isError?: boolean;
}): OnboardingHistory {
  if (q.isError) return 'unknown';
  if (q.data === undefined) return q.isLoading || q.isFetching ? 'loading' : 'unknown';
  const n = q.data?.stats?.totalSessions;
  if (typeof n !== 'number' || !Number.isFinite(n)) return 'unknown';
  return n > 0 ? 'some' : 'none';
}

export interface OnboardingWelcomeInput {
  entry: OnboardingEntry;
  /** GET /onboarding/me answered (not loading, not failed). */
  profileRead: boolean;
  hasCompletedOnboarding: boolean;
  retakeRequested: boolean;
  /** Central user and workspace both known. */
  identityKnown: boolean;
  started: boolean;
  history: OnboardingHistory;
}

/** 'welcome' | 'checking' (history read in flight: spinner) | 'none' (the page as before). */
export type OnboardingWelcomeView = 'welcome' | 'checking' | 'none';

export function onboardingWelcomeView(input: OnboardingWelcomeInput): OnboardingWelcomeView {
  if (input.entry !== 'wizard' || !input.profileRead || input.hasCompletedOnboarding || input.retakeRequested) {
    return 'none';
  }
  if (!input.identityKnown || input.started) return 'none';
  if (input.history === 'loading') return 'checking';
  return input.history === 'none' ? 'welcome' : 'none';
}
